/* eslint-disable no-nested-ternary */
import React, { useEffect, useRef, useState } from 'react';
import { Room } from '@mui/icons-material';
import { OutlinedInput, Paper, Popper } from '@mui/material';
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng
} from 'react-places-autocomplete';

import { useTheme } from 'contexts/ThemeContext';

import { Divider, Text } from 'components/DataDisplay';
import { Item, Row } from 'components/Layout';

export const LocationSearchInput = ({
  onPlaceSelected,
  title,
  required = false,
  error,
  color,
  adr = '',
  disabled = false,
  optionWidth
}) => {
  const { themeColors, panelColor } = useTheme();
  const [address, setAdress] = useState(adr);
  const [anchorEl, setAnchorEl] = useState(null);
  const inputRef = useRef(null);

  const colorRules = themeColors[color] || color || themeColors[panelColor];

  const handleChange = value => {
    setAnchorEl(inputRef.current);
    setAdress(value);
  };

  const handleSelect = value => {
    handleChange(value);
    geocodeByAddress(value)
      .then(results => {
        getLatLng(results[0]).then(({ lat, lng }) => {
          onPlaceSelected({ lat, lng, address: value });
        });
      })
      .catch(e => console.error('Error', e));
  };

  useEffect(() => {
    if (adr !== address) {
      setAdress(adr);
    }
    // eslint-disable-next-line
  }, [adr]);

  return (
    <PlacesAutocomplete
      value={address}
      onChange={handleChange}
      onSelect={handleSelect}
      disabled={disabled}
    >
      {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => {
        const paperWidth =
          suggestions.length > 0
            ? optionWidth || `${inputRef.current?.offsetWidth}px`
            : 0;

        return (
          <>
            {title && (
              <Row spacing={2} justify="flex-start" alignItems="center">
                <Item justify="flex-start" flex>
                  <Text
                    noWrap
                    color={error ? 'error' : null}
                    fontSize="14px"
                    style={{
                      marginTop: '5px',
                      marginBottom: '1px'
                    }}
                  >
                    {title === '&nbsp;' ? <>&nbsp;</> : title}
                    {required && '*'}
                  </Text>
                </Item>
              </Row>
            )}
            <OutlinedInput
              {...getInputProps({})}
              disabled={disabled}
              sx={{
                borderRadius: '5px',
                height: '40px',
                width: '100%',
                backgroundColor: disabled
                  ? themeColors.tertiary
                  : themeColors.white,
                border: 'none',
                padding: '1em',
                fontSize: '12px',
                outline: 'none',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: `${
                    disabled
                      ? themeColors.disabled
                      : error
                      ? '#d32f2f'
                      : colorRules
                  } !important`
                }
              }}
              ref={inputRef}
            />
            <Popper
              open={suggestions.length > 0}
              anchorEl={anchorEl}
              placement="bottom-start"
              disablePortal
              style={{
                zIndex: 200
              }}
            >
              <Paper
                style={{
                  maxWidth: paperWidth,
                  minWidth: paperWidth,
                  width: paperWidth,
                  padding: '10px'
                }}
              >
                {loading && <Text>Loading...</Text>}
                {suggestions.map(suggestion => {
                  const className = suggestion.active
                    ? 'suggestion-item--active'
                    : 'suggestion-item';
                  const style = suggestion.active
                    ? {
                        backgroundColor: themeColors.tertiary,
                        cursor: 'pointer',
                        maxWidth: `calc(${paperWidth} - 30px)`
                      }
                    : {
                        backgroundColor: themeColors.secondary,
                        cursor: 'pointer',
                        maxWidth: `calc(${paperWidth} - 30px)`
                      };
                  return (
                    <>
                      <Row spacing={0} justify="flex-start">
                        <Item flex>
                          <Room size="small" />
                        </Item>
                        <Item
                          justify="flex-start"
                          alignItems="center"
                          {...getSuggestionItemProps(suggestion, {
                            className,
                            style
                          })}
                          flex
                        >
                          <Text noWrap>{suggestion.description}</Text>
                        </Item>
                      </Row>
                      <Row spacing={0}>
                        <Divider
                          style={{
                            width: '100%',
                            height: '1px'
                          }}
                        />
                      </Row>
                    </>
                  );
                })}
              </Paper>
            </Popper>
          </>
        );
      }}
    </PlacesAutocomplete>
  );
};

export default LocationSearchInput;
